/**
 * Parses a list of CLI args and maps them back to module version inputs,
 * coercing each value to its declared type. Positional args are matched
 * by position, optional args are matched by flag name (e.g. --flag).
 * A flag present without an explicit value (e.g. --verbose) will be coerced to true.
 *
 * @param {Object<string, {
 *   position: number|null,
 *   type: "string"|"float"|"int"|"boolean",
 *   nargs: number|"+"|"?"|null,
 *   value: *,
 *   argName: string,
 * }>} moduleVersionInputs
 * @param {string[]} args
 * @returns {Object<string, {
 *   position: number|null,
 *   type: "string"|"float"|"int"|"boolean",
 *   nargs: number|"+"|"?"|null,
 *   value: *,
 *   argName: string,
 * }>} A deep copy of moduleVersionInputs with values updated to the parsed and type-coerced args.
 */
export function parseArgsToInputValues(moduleVersionInputs, args) {
    // Deep copy moduleVersionInputs to avoid modifying the input argument
    const inputs = structuredClone(moduleVersionInputs);

    // Add input keys on input objects for easier reference
    Object.entries(inputs).forEach(([key, input]) => {
        input.key = key;
    });

    const positionalInputs = Object.values(inputs).filter(
        (i) => i.position != null
    );
    const optionalInputs = Object.values(inputs).filter(
        (i) => i.position == null
    );

    const firstFlagIndex = args.findIndex((a) => a.startsWith("--"));
    const positionalArgs =
        firstFlagIndex === -1 ? [...args] : args.slice(0, firstFlagIndex);
    const optionalArgs =
        firstFlagIndex === -1 ? [] : args.slice(firstFlagIndex);

    const mappedValues = {
        ...mapPositionalArgs(positionalInputs, positionalArgs),
        ...mapOptionalArgs(optionalInputs, optionalArgs),
    };

    Object.entries(inputs).forEach(([key, input]) => {
        if (!(key in mappedValues)) return;
        const raw = mappedValues[key];
        // A positional the pipeline left empty means "the user supplies this",
        // so leave it unset rather than coercing. This must short-circuit
        // before coerceValue, which would turn "" into 0 for an int or float.
        const isEmptyPositional =
            input.position != null &&
            (raw === "" || (Array.isArray(raw) && raw.every((v) => v === "")));
        input.value = isEmptyPositional ? null : coerceValue(raw, input.type);
    });

    // Remove input keys from input objects before returning
    Object.values(inputs).forEach((input) => {
        delete input.key;
    });
    return inputs;
}

/**
 * Maps positional args to their corresponding input keys based on position and nargs.
 *
 * @param {{
 *   position: number,
 *   nargs: number|"+"|null,
 *   key: string,
 * }[]} positionalInputs
 * @param {string[]} positionalArgs
 * @returns {Object<string, string|string[]>}
 */
function mapPositionalArgs(positionalInputs, positionalArgs) {
    const inputs = structuredClone(positionalInputs);
    inputs.forEach((input) => {
        if (typeof input.nargs === "string" && input.nargs !== "+") {
            throw new Error(`Unimplemented nargs value: "${input.nargs}"`);
        }
    });

    const sorted = inputs.sort((a, b) => a.position - b.position);
    const result = {};
    let argIndex = 0;

    for (let i = 0; i < sorted.length; i++) {
        const input = sorted[i];

        if (input.nargs === "+") {
            // Count how many args the remaining fixed inputs need
            const reservedAfter = sorted.slice(i + 1).reduce((sum, inp) => {
                return sum + (inp.nargs == null ? 1 : inp.nargs);
            }, 0);
            const available = positionalArgs.length - argIndex - reservedAfter;
            result[input.key] = positionalArgs.slice(
                argIndex,
                argIndex + available
            );
            argIndex += available;
        } else if (input.nargs == null) {
            // Python default: consumes 1 arg, stored as a scalar
            result[input.key] = positionalArgs[argIndex];
            argIndex += 1;
        } else {
            // Explicit numeric nargs (including 1): stored as an array
            result[input.key] = positionalArgs.slice(
                argIndex,
                argIndex + input.nargs
            );
            argIndex += input.nargs;
        }
    }

    return result;
}

/**
 * Maps optional args to their corresponding input keys based on argName and nargs.
 *
 * Note that when a flag is not followed by any value (and nargs is "?"),
 * it is stored as `null`.
 *
 * @param {{
 *   argName: string,
 *   nargs: number|"+"|"?"|null,
 *   key: string,
 * }[]} optionalInputs
 * @param {string[]} optionalArgs
 * @returns {Object<string, string|string[]|null>}
 */
function mapOptionalArgs(optionalInputs, optionalArgs) {
    const inputs = structuredClone(optionalInputs);
    inputs.forEach((input) => {
        if (
            typeof input.nargs === "string" &&
            input.nargs !== "+" &&
            input.nargs !== "?"
        ) {
            throw new Error(`Unimplemented nargs value: "${input.nargs}"`);
        }
    });

    const inputByArgName = {};
    inputs.forEach((input) => {
        inputByArgName[input.argName] = input;
    });

    const isFlag = (token) => token != null && token.startsWith("--");

    // Split optionalArgs into [flag, ...values] groups, using the next flag
    // (or end of stream) as the natural delimiter.
    const groups = [];
    for (let i = 0; i < optionalArgs.length; i++) {
        const token = optionalArgs[i];
        if (!isFlag(token)) {
            throw new Error(`Expected a flag, got: "${token}"`);
        }
        const start = i + 1;
        let end = start;
        while (end < optionalArgs.length && !isFlag(optionalArgs[end])) {
            end += 1;
        }
        groups.push({ flag: token, values: optionalArgs.slice(start, end) });
        i = end - 1;
    }

    const result = {};
    groups.forEach(({ flag, values }) => {
        const input = inputByArgName[flag];
        if (input == null) {
            throw new Error(`Unknown flag: "${flag}"`);
        }
        if (input.key in result) {
            throw new Error(`Duplicate flag: "${flag}"`);
        }

        if (input.nargs === "+") {
            if (values.length < 1) {
                throw new Error(`Flag "${flag}" requires at least one value`);
            }
            result[input.key] = values;
        } else if (input.nargs === "?") {
            if (values.length > 1) {
                throw new Error(`Flag "${flag}" takes at most one value`);
            }
            result[input.key] = values.length === 1 ? values[0] : null;
        } else if (input.nargs == null) {
            if (values.length !== 1) {
                throw new Error(`Flag "${flag}" requires exactly one value`);
            }
            result[input.key] = values[0];
        } else {
            // Explicit numeric nargs (including 1): stored as an array
            if (values.length !== input.nargs) {
                throw new Error(
                    `Flag "${flag}" requires ${input.nargs} values`
                );
            }
            result[input.key] = values;
        }
    });

    return result;
}

/**
 * Coerces a raw CLI value to the specified type.
 * Handles arrays (multiple values), null (flag with no value), and single scalars.
 * A null value means the flag was activated without an explicit value (e.g. --verbose),
 * which is coerced to true.
 *
 * @param {string|string[]|null} raw
 * @param {"string"|"float"|"int"|"boolean"} type
 * @returns {string|number|boolean
 *   |Array<string|number|boolean>}
 *   The coerced value, or an array of coerced values
 *   if raw is an array.
 */
function coerceValue(raw, type) {
    if (Array.isArray(raw)) return raw.map((v) => coerceScalar(v, type));
    if (raw === null) return true; // flag activated, no value
    return coerceScalar(raw, type);
}

/**
 * @param {string} raw
 * @param {"string"|"float"|"int"|"boolean"} type
 * @returns {string|number|boolean}
 */
function coerceScalar(raw, type) {
    switch (type) {
        case "string":
            return raw;
        case "int": {
            const n = Number(raw);
            if (!Number.isInteger(n)) {
                throw new Error(`Cannot coerce "${raw}" to int`);
            }
            return n;
        }
        case "float": {
            const n = Number(raw);
            if (Number.isNaN(n)) {
                throw new Error(`Cannot coerce "${raw}" to float`);
            }
            return n;
        }
        case "boolean": {
            const lower = raw.toLowerCase();
            if (lower === "true" || lower === "1") return true;
            if (lower === "false" || lower === "0") return false;
            throw new Error(`Cannot coerce "${raw}" to boolean`);
        }
        default:
            throw new Error(`Unknown type: "${type}"`);
    }
}
